import moment from "moment/moment";
import type { AddToCartEventProps } from "../Marketplace/lib/add-to-cart";

// Webtrack.cshtml
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[] | undefined;
  }
}

// od.dates.js, various
declare var OD: any;

export class WebtrackContainer {
  constructor(enabled: boolean) {
    this.#trackingEnabled = enabled;

    enabled &&
      document.addEventListener(
        "add-to-cart",
        ({ detail }: CustomEvent<AddToCartEventProps>) => {
          if (detail.source === "titles") {
            this.trackAddToCart(
              detail.titles.map(({ id, pdmo, units }) => ({
                titleId: id,
                pdmo,
                units,
              })),
              detail.cartId,
              // At the moment, this event is never fired from a page containing multiple lists (that only happens with carousels).
              // When we start doing that, we'll need to provide listId
              undefined,
            );
          }
        },
      );
  }

  #productClickElements = "";
  #trackingEnabled = false;
  #webtrackTitles: { key: number; value: WebtrackTitle }[] = [];

  //Track all product views (cover image click, title link click, etc)
  #initProductClickTrackingEvents() {
    $(this.#productClickElements).on("click", () => {
      // get the id of the product from the element's data attribute
      var itemId = $(this).attr("data-wtitemid")!;
      var listId = $(this).attr("data-wtlistid");
      this.trackProductClick(itemId, false, listId);
    });
  }

  // don't assign any handlers if no elements passed into assignment
  assignProductClickElements(elements: string) {
    if (typeof elements === "undefined" || elements.length == 0) {
      return;
    }
    this.#productClickElements = elements;
    this.#initProductClickTrackingEvents();
  }

  // track initial user login
  trackLogin() {
    this.#initDataLayer(trackingEvents.LOGIN);
  }

  // initialize the data layer on page load
  initPageLoad() {
    this.#initDataLayer();
  }

  addTitles(titles: WebtrackTitleObject[], allowDupes: boolean) {
    if (typeof allowDupes === "undefined" || allowDupes === null) {
      allowDupes = false;
    }

    titles.forEach((title) => {
      var wtTitle: WebtrackTitle = {
        ...title,
        // When a search has been executed, preface search terms with the word "Search:" so the 'list' property can easily be distinguished b/t search results and featured list
        SearchTerms: `Search: ${title.SearchTerms ?? ""}`,
      };
      this.#addTitle(wtTitle, allowDupes);
    });
  }

  // update quantity value for a given title
  updateTitleQuantity(titleId: string | number, quantity: string | number) {
    var updateTitle = this.#findWebtrackTitle(titleId);
    if (updateTitle) {
      updateTitle.Quantity = Number(quantity);
    }
  }

  // track when all title quantities are being updated to a specific value
  trackUpdateAllQuantities(
    cartId: unknown,
    quantity: unknown,
    loadSu: boolean,
  ) {
    if (!$.isNumeric(cartId) || !$.isNumeric(quantity)) {
      return;
    }
    $.ajax({
      type: "POST",
      url:
        "/Webtrack/TrackCartUpdateAllQuantities?cartId=" +
        cartId +
        "&quantity=" +
        quantity +
        "&loadSimUse=" +
        loadSu,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvents.ADDTOCART);
        }
      },
    });
  }

  // update distribution model value for a given title
  updateTitleDistributionModel(
    titleId: string | number,
    disModelId: string | number,
  ) {
    var updateTitle = this.#findWebtrackTitle(titleId);

    if (updateTitle) {
      updateTitle.DistributionModelId = Number(disModelId);
    }
  }

  // add a single webtrack title to the collection
  #addTitle(titleObj: WebtrackTitle, allowDupes: boolean) {
    // only add the title if it doesn't already exist in the array,
    // or if duplicates are explicitly allowed
    if (allowDupes || !this.#arrayContainsTitle(titleObj.CrossRefId)) {
      this.#webtrackTitles.push({
        key: titleObj.CrossRefId,
        value: titleObj,
      });
    }
  }

  // returns whether or not a title (by identifier) already exists in the array
  #arrayContainsTitle(titleId: number) {
    return (
      this.#webtrackTitles.find((title) => title.key === titleId) !== undefined
    );
  }

  // searches the array for a title by crossRefId
  #findWebtrackTitle(titleId: string | number): WebtrackTitle | null {
    var title = $.grep(this.#webtrackTitles, (e) => e.key == titleId);

    return title[0] && title[0].value ? title[0].value : null;
  }

  #findWebtrackTitleByListId(
    titleId: string | number,
    listId: string | number,
  ): WebtrackTitle | null {
    var title = $.grep(
      this.#webtrackTitles,
      (e) => e.key == titleId && e.value.ListId == listId,
    );

    return title[0] && title[0].value ? title[0].value : null;
  }

  // track a single title (product view)
  trackTitleDetail(titleId: number) {
    $.ajax({
      url: "/Webtrack/TrackTitleDetails?itemId=" + titleId,
      async: false, // (CRVTWO-7746) title details (productView) tracking must be done synchronously, so that the push occurs before the global GTM container is pushed
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          //CRVTWO-9503 track title details as impressions event
          this.#doDataPush(response, true, trackingEvents.IMPRESSIONS);
        }
      },
    });
  }

  // track a product click
  trackProductClick(
    titleId: string | number,
    trackById: boolean,
    listId: string | number | undefined,
  ) {
    if (!(typeof trackById === "undefined") && trackById === true) {
      this.#trackTitleById(titleId, trackingEvents.PRODUCTCLICK);
    } else {
      var title;
      if (!(typeof listId === "undefined")) {
        title = this.#findWebtrackTitleByListId(titleId, listId);
      } else {
        title = this.#findWebtrackTitle(titleId);
      }

      if (title !== null) {
        title = JSON.stringify({ title: title });
        $.ajax({
          type: "POST",
          url: "/Webtrack/TrackProductClick",
          data: title,
          async: true,
          contentType: "application/json; charset=utf-8",
          dataType: "json",
          success: (response) => {
            if (response.Success === true) {
              this.#doDataPush(response, true, trackingEvents.PRODUCTCLICK);
            }
          },
        });
      }
    }
  }

  // track a collection of title impressions
  trackTitleImpressions() {
    var titles = this.#webtrackTitles.map((title) => title.value);
    this.#trackImpressions(titles);
  }

  // track titles that become visible when a carousel prev/next arrow is clicked
  trackCarouselClick(titleIdList: string) {
    var titleArray: WebtrackTitle[] = [];
    var idArray = titleIdList.split(",");
    // retrieve all titles being viewed/made visible in the carousel and add them to the array to be sent into the tracking call
    idArray.forEach((titleId) => {
      var title = this.#findWebtrackTitle(titleId);
      if (title) {
        titleArray.push(title);
      }
    });
    // identical to impression tracking, except that an event is passed in the data layer
    var titles = JSON.stringify({ titles: titleArray });
    $.ajax({
      type: "POST",
      url: "/Webtrack/TrackImpressions",
      data: titles,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvents.CAROUSELCLICK);
        }
      },
    });
  }

  trackCreateCart(cart: {
    cartId: unknown;
    cartName: unknown;
    cartType: unknown;
  }) {
    var cartJson = JSON.stringify({ cart: cart });

    $.ajax({
      type: "POST",
      url: "/Webtrack/TrackCreateCart",
      data: cartJson,
      async: true,
      contentType: "application/json; charset=urt-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          response.content = {
            cartID: response.content.cartID.toString(),
            cartName: response.content.cartName,
            cartType: response.content.cartType,
          };
          this.#doDataPush(response, false, trackingEvents.CREATECART);
        }
      },
    });
  }

  // track add to cart for any number of titles
  trackAddToCart(
    titleIdList:
      | string
      | {
          titleId: number;
          pdmo?: number | undefined;
          units: number;
        }[],
    cartId: number,
    listId?: number | undefined,
  ) {
    // don't track if adding all search results to cart
    if (titleIdList == "*") {
      return;
    }

    // actually an array of titles, but specifying the title type will spiral out across this entire file
    // (which should be done eventually!)
    var titleArray: WebtrackTitle[] = [];

    var idArray: (
      | Pick<WebtrackTitle, "CrossRefId">
      | Pick<WebtrackTitle, "CrossRefId" | "DistributionModelId" | "Quantity">
    )[] =
      typeof titleIdList === "string"
        ? titleIdList.split(",").map((id) => ({ CrossRefId: parseInt(id) }))
        : titleIdList.map(({ titleId, pdmo, units }) => ({
            CrossRefId: titleId,
            DistributionModelId: pdmo ?? -1,
            Quantity: units,
          }));

    // retrieve all titles being added to the cart and add them to the array to be sent into the tracking call
    idArray.forEach((title) => {
      var titleInfo: WebtrackTitle | null;
      // if featured list id is provided, use it to retrieve title
      if (!(typeof listId === "undefined")) {
        titleInfo = this.#findWebtrackTitleByListId(title.CrossRefId, listId);
      } else {
        titleInfo = this.#findWebtrackTitle(title.CrossRefId);
      }
      if (!titleInfo) {
        return;
      }

      // it's possible to add a title to a cart w/o a quantity value (empty box). In this case, set the quantity value to 1
      titleInfo.Quantity = titleInfo.Quantity ?? 1;

      titleArray.push({
        ...titleInfo,
        ...title,
        CartId: cartId,
      });
    });

    const titlesString = JSON.stringify({ titles: titleArray });
    $.ajax({
      type: "POST",
      url: "/Webtrack/TrackAddToCart",
      data: titlesString,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvents.ADDTOCART);
        }
      },
    });
  }

  trackRemoveFromCart(titleIdList: string) {
    var titleArray: WebtrackTitle[] = [];
    var idArray = titleIdList.split(",");
    // retrieve all titles being added to the cart and add them to the array to be sent into the tracking call
    idArray.forEach((titleId) => {
      var title = this.#findWebtrackTitle(titleId);
      if (title) {
        titleArray.push(title);
      }
    });
    var titles = JSON.stringify({ titles: titleArray });
    $.ajax({
      type: "POST",
      url: "/Webtrack/TrackRemoveFromCart",
      data: titles,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvents.REMOVEFROMCART);
        }
      },
    });
  }

  trackCheckout(cartId: number, step: number, loadSu?: boolean) {
    var trackUrl = "/Webtrack/TrackCheckout?cartId=" + cartId + "&step=" + step;
    // if necessary assign flag so controller/provider knows to load SU titles
    if (!(typeof loadSu === "undefined")) {
      trackUrl += "&loadSimUse=true";
    }
    $.ajax({
      type: "POST",
      // using the step value as part of the track checkout actions name allows for one central 'checkout' function
      url: trackUrl,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvents.CHECKOUT);
        }
      },
    });
  }

  trackCheckoutPaymentOption(cartName: string, paymentOption: string) {
    if (!this.#trackingEnabled) {
      return;
    }

    // since no server-side data is necessary for this push, just do it directly within the method
    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var dataPush: Record<string, unknown> = {};
    dataPush["event"] = trackingEvents.CHECKOUTOPTION;
    dataPush["ecommerce"] = {
      checkout_option: {
        actionField: {
          step: 2,
          option: paymentOption, // pay by cc, content credit or bill my account
          list: cartName, // name of cart being purchased
        },
      },
    };
    dataLayer.push(dataPush);
  }

  trackCheckoutTransaction(orderId: string) {
    if (!this.#trackingEnabled) {
      return;
    }

    // call transaction method.
    // if success,
    // 1: push to datalayer
    // 2: call checkout step 3 method
    // 2.5: push to datalayer
    $.ajax({
      url: "/Webtrack/TrackCheckoutTransaction?identifier=" + orderId,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvents.CHECKOUTTRANSACTION);
          // checkout step 3 ('checkout') does not require any sort of data detail, so retrieve what's needed and do the push here
          var dataLayer = (window.dataLayer = window.dataLayer || []);
          var dataPush: Record<string, unknown> = {};
          dataPush["event"] = trackingEvents.CHECKOUT;
          dataPush["ecommerce"] = {
            checkout: {
              actionField: {
                step: 3, // Step 3 - confirmation
                list: response.ecommerce.purchase.actionField.list, // name of cart purchased
              },
            },
          };
          dataLayer.push(dataPush);
        }
      },
    });
  }

  trackClassSetCheckout(
    titleId: number,
    disModelId: number,
    userCount: number,
    checkoutStep: number,
  ) {
    var trackUrl =
      "/Webtrack/TrackClassSetCheckout?itemId=" +
      titleId +
      "&disModelId=" +
      disModelId +
      "&userCount=" +
      userCount +
      "&checkoutStep=" +
      checkoutStep;
    $.ajax({
      type: "POST",
      url: trackUrl,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          response.ecommerce.checkout.actionField.step = checkoutStep;
          this.#doDataPush(response, true, trackingEvents.CHECKOUT);
        }
      },
    });
  }

  trackGoogleClassroomClick(title: unknown) {
    // since no server-side data is necessary for this push, just do it directly
    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var dataPush: Record<string, unknown> = {};
    // datalayer/push defined per CRVTWO-13835
    dataPush["event"] = trackingEvents.GOOGLECLASSROOM;
    dataPush["eventCategory"] = "Engagements";
    dataPush["eventAction"] = "Google Classroom";
    dataPush["eventLabel"] = title;

    dataLayer.push(dataPush);
  }

  trackGeneralPref(objForm: {
    // GeneralPreferenceSettings.cs
    PreferredLanguageCode: unknown;
  }) {
    // since no server-side data is necessary for this push (it can be derived from the form), just do it directly
    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var dataPush: Record<string, unknown> = {};
    // datalayer/push defined per CRVTWO-14443
    dataPush["event"] = trackingEvents.GENERALPREF;
    dataPush["user"] = {
      preferences: {
        general: {
          language: objForm["PreferredLanguageCode"],
        },
      },
    };
    dataLayer.push(dataPush);
  }

  trackSearchPresets(params: {
    presetUsage: unknown;
    languages: string[] | undefined;
    formatTypes: string[] | undefined;
    audiences: string[] | undefined;
    includePreorders: boolean | undefined;
    selectedSortBy: unknown;
    pageSize: string | number;
    hideMatureContent: boolean;
  }) {
    // since no server-side data is necessary for this push (it can be derived from the form), just do it directly
    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var dataPush: Record<string, unknown> = {};
    // datalayer/push defined per CRVTWO-14443
    dataPush["event"] = trackingEvents.SEARCHPRESETS;
    dataPush["user"] = {
      preferences: {
        search: {
          type: params.presetUsage,

          language:
            params.languages !== undefined
              ? params.languages.join(", ")
              : "all",

          format:
            params.formatTypes !== undefined
              ? params.formatTypes.join(", ")
              : "all",

          audience:
            params.audiences !== undefined
              ? params.audiences.join(", ")
              : "all",

          releaseStatus:
            params.includePreorders !== undefined
              ? params.includePreorders === true
                ? "preorder"
                : "released"
              : "show all",

          sort: params.selectedSortBy,

          perpage: params.pageSize,

          hideMature: params.hideMatureContent,
        },
      },
    };
    dataLayer.push(dataPush);
  }

  trackShelfTalker(
    objForm: {
      // ShelfTalkersViewModel.cs
      ShelfTalkerOption: string;
      HasAttributeInput: "true" | "false";
      ScheduledStart: string;
      ScheduledEnd: string;
    },
    titleIds: string,
  ) {
    // since no server-side data is necessary for this push (it can be derived from the form), just do it directly
    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var dataPush: Record<string, unknown> = {};

    // combine message type/shelf talker option when appropriate
    var messageType = "ST: " + objForm["ShelfTalkerOption"];

    // datalayer/push defined per CRVTWO-14652
    if (titleIds.split(";").length > 1) {
      dataPush["event"] = trackingEvents.BULKSHELFTALKER;
    } else {
      dataPush["event"] = trackingEvents.SHELFTALKER;
    }
    dataPush["titleID"] = titleIds;
    dataPush["noteType"] = messageType;
    // HasAttributeInput is false when no attribute exists, but is an array when attribute info does exist, so check that and flatten to true/false
    dataPush["attribute"] =
      objForm["HasAttributeInput"] === "false" ? false : true;
    // format dates as US since they will only be referenced by OD
    if ((objForm["ScheduledStart"] || "").length > 0) {
      dataPush["scheduleStart"] = moment(
        objForm["ScheduledStart"],
        OD.MarketplaceDates.momentJsDateFormat,
      ).format("M/D/YYYY");
    } else {
      dataPush["scheduleStart"] = "";
    }
    if ((objForm["ScheduledEnd"] || "").length > 0) {
      dataPush["scheduleEnd"] = moment(
        objForm["ScheduledEnd"],
        OD.MarketplaceDates.momentJsDateFormat,
      ).format("M/D/YYYY");
    } else {
      dataPush["scheduleEnd"] = "";
    }

    dataLayer.push(dataPush);
  }

  // initialize datalayer upon page load
  #initDataLayer(event?: string) {
    $.ajax({
      url: "/Webtrack/Init",
      async: false,
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, false, event);
        }
      },
    });
  }

  // assemble the components of the datalayer and send to web tracking
  #doDataPush(
    response: { user: unknown; content: unknown; ecommerce: unknown }, //WebtrackDataLayerObject.cs
    includeEcommerce: boolean,
    trackingEvent?: string | undefined,
  ) {
    if (!this.#trackingEnabled) {
      return;
    }

    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var dataPush: Record<string, unknown> = {};
    // user and content should only be populated once per page load -- do not add to push if they are null
    if (!(response.user === null) && !(typeof response.user === "undefined")) {
      dataPush["user"] = response.user;
    }
    if (
      !(response.content === null) &&
      !(typeof response.content === "undefined")
    ) {
      dataPush["content"] = response.content;
    }
    // TODO nbeaudoin: per CRVTWO-7318 we aren't retrieving the 'totCarts' value currently, so do not add session object to push.  Possibly to be added at a future date
    //dataPush['session'] = response.session;
    if (includeEcommerce) {
      dataPush["ecommerce"] = response.ecommerce;
    }
    if (!(typeof trackingEvent === "undefined")) {
      dataPush["event"] = trackingEvent;
    }

    dataLayer.push(dataPush);
  }

  #trackImpressions(titles: WebtrackTitle[]) {
    var titlesJson = JSON.stringify({ titles: titles });

    $.ajax({
      type: "POST",
      url: "/Webtrack/TrackImpressions",
      data: titlesJson,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvents.IMPRESSIONS);
        }
      },
    });
  }

  // tracks a title by id, rather than passing a title object
  #trackTitleById(titleId: unknown, trackingEvent: string | undefined) {
    $.ajax({
      type: "GET",
      url: "/Webtrack/TrackProductClickById?id=" + titleId,
      async: true,
      contentType: "application/json; charset=utf-8",
      dataType: "json",
      success: (response) => {
        if (response.Success === true) {
          this.#doDataPush(response, true, trackingEvent);
        }
      },
    });
  }
}

// Matches WebtrackTitleObject.cs
type WebtrackTitleObject = {
  ReserveId: string;
  CrossRefId: number;
  DistributionModelId: number;
  Quantity: number;
  Position: number;
  CartId: number;
  SearchTerms: string;
  ListName: string;
  ListId: number;
};

// represents a single title
type WebtrackTitle = {
  ReserveId: string;
  CrossRefId: number;
  DistributionModelId: number;
  Quantity: number;
  Position: number;
  CartId: number;
  // SearchTerms will have a special value compared to WebtrackTitleObject
  SearchTerms: string;
  ListName: string;
  ListId: number;
};

// tracking events 'enum'
const trackingEvents = {
  DETAIL: "detail",
  ADDTOCART: "addToCart",
  REMOVEFROMCART: "removeFromCart",
  CHECKOUT: "checkout",
  CHECKOUTOPTION: "checkoutOption",
  CHECKOUTTRANSACTION: "transaction",
  PRODUCTCLICK: "productClick",
  CAROUSELCLICK: "carouselClick",
  LOGIN: "login",
  IMPRESSIONS: "impressionTrack",
  PROMOCLICK: "promotionClick",
  CREATECART: "createCart",
  GOOGLECLASSROOM: "googleClassClick",
  // preferences update:
  SEARCHPRESETS: "searchPresets",
  GENERALPREF: "generalPref",
  EMAILPREF: "emailPref",
  SHELFTALKER: "shelfTalker",
  BULKSHELFTALKER: "bulkShelfTalker",
};
